/**
 * The workspace prompt library's callables, from the browser (phase 2,
 * 2026-09-17).
 *
 * WHY THERE ARE FIVE HERE AND NO READS. Both shelves are READ straight from
 * Firestore — the person's own under the owner-uid grant, the workspace's under
 * a rule that allows `get` and `list` to a verified member holding
 * `prompts.library.view` — so the picker subscribes live and opens instantly.
 * Every WRITE to the workspace shelf is denied to clients outright and goes
 * through `functions/src/promptLibrary/promptCores.ts`, which holds the whole
 * ladder: the lever, the account resolution, the permission, the
 * author-or-curator predicate, the validator and the transaction. Prest0n's
 * confirmed card runs those same cores, as the person who tapped — which is
 * what makes "under your own permissions" structural rather than promised.
 *
 * EVERY REFUSAL IS THE SERVER'S OWN WORDS. A core throws an `HttpsError` whose
 * message is written second-person and names the one thing that would clear it;
 * this file passes that message through untouched rather than mapping codes to
 * a second set of sentences, because two sets drift and the person then reads
 * whichever one is stale. `PromptCallResult.message` is that text; `ok` is the
 * only thing the caller branches on.
 */
import { getFunctions, httpsCallable, type HttpsCallableResult } from 'firebase/functions';
import { getApp } from 'firebase/app';

/** The default app instance, so Functions shares auth state with Auth. */
const fns = () => getFunctions(getApp());

export interface PromptCallResult<T> {
  ok: boolean;
  value?: T;
  /** The server's own second-person refusal, verbatim. Empty when `ok`. */
  message: string;
}

/**
 * Call one, and turn a throw into a verdict.
 *
 * A caller in a picker never wants an exception: a failed share must leave the
 * list exactly as it was with one line of explanation, not unmount the surface
 * somebody is mid-compose in.
 */
async function call<T>(name: string, payload: Record<string, unknown>): Promise<PromptCallResult<T>> {
  try {
    const result = await httpsCallable(fns(), name)(payload) as HttpsCallableResult<T>;
    return { ok: true, value: result.data, message: '' };
  } catch (err: unknown) {
    const message = String((err as { message?: string })?.message || '').trim();
    return {
      ok: false,
      // A transport failure carries no server sentence; say the honest thing
      // rather than inventing a reason the server never gave.
      message: message || 'That did not go through. Try again in a moment.',
    };
  }
}

export interface SharedPromptRef { accountId: string; sharedPromptId: string; title?: string }

/** Publish one of your own prompts to the workspace library. */
export const sharePrompt = (accountId: string, promptId: string) =>
  call<SharedPromptRef>('promptsShare', { accountId, promptId });

/** Push your current version over the copy the workspace reads. */
export const republishPrompt = (accountId: string, promptId: string) =>
  call<SharedPromptRef>('promptsRepublish', { accountId, promptId });

/** Take your own prompt back out of the workspace library. */
export const unsharePrompt = (accountId: string, promptId: string) =>
  call<SharedPromptRef>('promptsUnshare', { accountId, promptId });

/** Edit a prompt in the workspace library in place (yours, or anyone's with curate). */
export const updateSharedPrompt = (
  accountId: string,
  sharedPromptId: string,
  draft: { title: string; body: string; category?: string },
) => call<SharedPromptRef>('promptsUpdateShared', { accountId, sharedPromptId, ...draft });

/** Remove a prompt from the workspace library (yours, or anyone's with curate). */
export const deleteSharedPrompt = (accountId: string, sharedPromptId: string) =>
  call<SharedPromptRef>('promptsDeleteShared', { accountId, sharedPromptId });
